/**
 * AdminCustomers — 客户管理模块
 * - 展示所有客户，支持搜索
 * - 新增客户前校验手机/公司/邮箱是否重复（复购识别）
 * - 重复时提示已有客户信息，可选"强制保存"或"重新填写"
 */
import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RefreshCw, Search, AlertCircle, Plus, Users,
  Building2, Phone, Mail, CheckCircle2, Loader2, X,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const HEADER_BG = '#F0F2F5';
const ROW_HOVER = 'rgba(26,35,55,0.04)';

const INPUT_STYLE: React.CSSProperties = {
  background: '#FFFFFF', border: '1px solid #D0D5E2',
  color: '#1A2337', borderRadius: '4px',
  padding: '8px 12px', fontSize: '13px', outline: 'none', width: '100%',
};

interface Customer {
  id: string;
  name: string | null;
  company: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  source_inquiry_id: string | null;
  created_at: string;
}

interface DuplicateResult {
  field: string;
  value: string;
  existing: Customer;
}

// ── 新建客户弹窗 ──
function AddCustomerModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [err, setErr] = useState('');
  const [duplicates, setDuplicates] = useState<DuplicateResult[]>([]);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const checkDuplicates = async (): Promise<DuplicateResult[]> => {
    setChecking(true);
    const results: DuplicateResult[] = [];
    const { data: all } = await supabase.from('customers').select('*');
    if (all) {
      for (const c of all as Customer[]) {
        if (form.phone.trim() && c.phone === form.phone.trim())
          results.push({ field: '手机号', value: form.phone.trim(), existing: c });
        if (form.email.trim() && c.email === form.email.trim())
          results.push({ field: '邮箱', value: form.email.trim(), existing: c });
        if (form.company.trim() && c.company === form.company.trim())
          results.push({ field: '公司名称', value: form.company.trim(), existing: c });
      }
    }
    setChecking(false);
    return results;
  };

  const handleSave = async (force = false) => {
    if (!form.name.trim()) { setErr('姓名不能为空'); return; }
    setSaving(true); setErr(''); setDuplicates([]);

    if (!force) {
      const dups = await checkDuplicates();
      if (dups.length > 0) {
        setDuplicates(dups);
        setSaving(false);
        return;
      }
    }

    const { error } = await supabase.from('customers').insert([{
      name: form.name.trim(),
      company: form.company.trim() || null,
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      notes: form.notes.trim() || null,
    }]);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)' }}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-sm border border-border overflow-hidden"
        style={{ background: '#FFFFFF' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border"
          style={{ background: HEADER_BG }}>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" style={{ color: ORANGE }} />
            <span className="text-sm font-bold" style={{ color: '#1A2337' }}>新建客户</span>
          </div>
          <button onClick={onClose} style={{ color: '#8A9BB5' }}><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {duplicates.length > 0 && (
            <div className="rounded-sm border border-yellow-400/30 p-3 space-y-2"
              style={{ background: 'rgba(234,179,8,0.08)' }}>
              <p className="text-xs font-bold text-yellow-400">⚠️ 检测到疑似复购客户</p>
              {duplicates.map((d, i) => (
                <p key={i} className="text-xs" style={{ color: '#1A2337' }}>
                  {d.field} <span className="font-mono text-yellow-400">"{d.value}"</span> 已存在于客户{' '}
                  <span className="font-bold">{d.existing.name}</span>
                  {d.existing.company ? `（${d.existing.company}）` : ''}
                </p>
              ))}
              <p className="text-xs" style={{ color: '#8A9BB5' }}>如确认是新客户，可点击「强制保存」</p>
              <div className="flex gap-2 pt-1">
                <button onClick={() => handleSave(true)} disabled={saving}
                  className="text-xs font-bold px-3 py-1.5 rounded-sm"
                  style={{ background: ORANGE, color: '#fff' }}>
                  强制保存
                </button>
                <button onClick={() => setDuplicates([])}
                  className="text-xs px-3 py-1.5 rounded-sm border border-border"
                  style={{ color: '#8A9BB5' }}>
                  重新填写
                </button>
              </div>
            </div>
          )}

          {err && <p className="text-xs text-red-400">{err}</p>}

          <div className="space-y-1">
            <label className="text-xs" style={{ color: '#8A9BB5' }}>姓名 *</label>
            <input style={INPUT_STYLE} value={form.name}
              onChange={e => set('name', e.target.value)} placeholder="客户姓名" />
          </div>
          <div className="space-y-1">
            <label className="text-xs" style={{ color: '#8A9BB5' }}>公司名称</label>
            <input style={INPUT_STYLE} value={form.company}
              onChange={e => set('company', e.target.value)} placeholder="所属公司" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs" style={{ color: '#8A9BB5' }}>手机号</label>
              <input style={INPUT_STYLE} value={form.phone}
                onChange={e => set('phone', e.target.value)} placeholder="联系电话" />
            </div>
            <div className="space-y-1">
              <label className="text-xs" style={{ color: '#8A9BB5' }}>邮箱</label>
              <input style={INPUT_STYLE} value={form.email}
                onChange={e => set('email', e.target.value)} placeholder="电子邮箱" />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs" style={{ color: '#8A9BB5' }}>备注</label>
            <textarea rows={3} style={{ ...INPUT_STYLE, resize: 'none' }} value={form.notes}
              onChange={e => set('notes', e.target.value)} placeholder="客户备注、需求偏好…" />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-border">
          <button onClick={onClose}
            className="text-xs px-4 py-2 rounded-sm border border-border"
            style={{ color: '#8A9BB5' }}>取消</button>
          <button onClick={() => handleSave(false)} disabled={saving || checking}
            className="flex items-center gap-2 text-sm font-bold px-5 py-2 rounded-sm disabled:opacity-60"
            style={{ background: ORANGE, color: '#fff' }}>
            {(saving || checking)
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <CheckCircle2 className="w-4 h-4" />}
            {checking ? '校验中…' : '保存客户'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── 主视图 ──
export function CustomersView() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchCustomers = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true); else setLoading(true);
    const { data } = await supabase
      .from('customers')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setCustomers(data as Customer[]);
    setLoading(false); setRefreshing(false);
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const filtered = customers.filter(c => !search ||
    [(c.name ?? ''), (c.company ?? ''), (c.phone ?? ''), (c.email ?? '')].some(v =>
      v.toLowerCase().includes(search.toLowerCase())
    )
  );

  const thisMonth = customers.filter(c =>
    new Date(c.created_at).getMonth() === new Date().getMonth() &&
    new Date(c.created_at).getFullYear() === new Date().getFullYear()
  ).length;

  return (
    <div className="space-y-6">
      {/* 统计卡片 */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: '客户总数', value: customers.length, color: 'text-blue-400' },
          { label: '本月新增', value: thisMonth, color: 'text-green-400' },
          { label: '有公司信息', value: customers.filter(c => c.company).length, color: 'text-orange-400' },
        ].map(item => (
          <div key={item.label} className="rounded-sm border border-border p-3"
            style={{ background: CARD_BG }}>
            <div className="text-[10px] font-mono mb-1" style={{ color: '#8A9BB5' }}>{item.label}</div>
            <div className={`text-2xl font-black ${item.color}`}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* 搜索 + 新建 */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            style={{ color: '#8A9BB5' }} />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="搜索姓名、公司、手机、邮箱…"
            className="w-full pl-9 pr-4 py-2 rounded-sm text-sm outline-none"
            style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337' }} />
        </div>
        <button onClick={() => fetchCustomers(true)} disabled={refreshing}
          className="p-2 rounded-sm border border-border disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            style={{ color: '#8A9BB5' }} />
        </button>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-sm"
          style={{ background: ORANGE, color: '#fff' }}>
          <Plus className="w-4 h-4" />新建客户
        </button>
      </div>

      {/* 客户列表 */}
      {loading ? (
        <div className="p-16 flex justify-center">
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: ORANGE }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-16 flex flex-col items-center gap-3 rounded-sm border border-border"
          style={{ background: CARD_BG, color: '#8A9BB5' }}>
          <AlertCircle className="w-10 h-10" />
          <span>暂无客户记录</span>
          <span className="text-xs">点击右上角「新建客户」开始录入</span>
        </div>
      ) : (
        <div className="rounded-sm border border-border overflow-hidden" style={{ background: CARD_BG }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border" style={{ background: HEADER_BG }}>
                {['姓名', '公司', '手机', '邮箱', '录入时间', ''].map(h => (
                  <th key={h}
                    className="text-left text-xs font-mono font-bold tracking-widest uppercase px-4 py-3"
                    style={{ color: ORANGE }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {filtered.map((c, i) => {
                  const isExpanded = expandedId === c.id;
                  const rowBg = i % 2 === 0 ? 'transparent' : '#F7F8FA';
                  const initial = (c.name ?? '').trim()[0]?.toUpperCase() ?? '?';
                  return (
                    <>
                      <tr key={c.id}
                        className="border-b border-border transition-colors"
                        style={{ background: rowBg }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = ROW_HOVER; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = rowBg; }}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                              style={{ background: `${ORANGE}20`, color: ORANGE }}>
                              {initial}
                            </div>
                            <span className="text-xs font-bold" style={{ color: '#1A2337' }}>{c.name ?? '—'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {c.company && <Building2 className="w-3 h-3 flex-shrink-0" style={{ color: '#8A9BB5' }} />}
                            <span className="text-xs" style={{ color: '#1A2337' }}>{c.company ?? '—'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {c.phone && <Phone className="w-3 h-3 flex-shrink-0" style={{ color: '#8A9BB5' }} />}
                            <span className="text-xs font-mono" style={{ color: '#1A2337' }}>{c.phone ?? '—'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {c.email && <Mail className="w-3 h-3 flex-shrink-0" style={{ color: '#8A9BB5' }} />}
                            <span className="text-xs" style={{ color: '#1A2337' }}>{c.email ?? '—'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#8A9BB5' }}>
                          {new Date(c.created_at).toLocaleDateString('zh-CN')}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : c.id)}
                            className="p-1.5 rounded-sm border border-border hover:border-orange-400/30 transition-colors">
                            {isExpanded
                              ? <ChevronUp className="w-3 h-3" style={{ color: '#8A9BB5' }} />
                              : <ChevronDown className="w-3 h-3" style={{ color: '#8A9BB5' }} />}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <motion.tr key={`${c.id}-detail`}
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                          <td colSpan={6} className="px-6 py-4 border-b border-border"
                            style={{ background: '#F7F8FA' }}>
                            <div className="text-xs space-y-1.5">
                              {c.notes && (
                                <p style={{ color: '#1A2337' }}>
                                  <span style={{ color: '#8A9BB5' }}>备注：</span>{c.notes}
                                </p>
                              )}
                              <p style={{ color: '#8A9BB5' }}>
                                来源：{c.source_inquiry_id
                                  ? <span className="font-mono text-blue-400">线索 {c.source_inquiry_id}</span>
                                  : '手动录入'}
                              </p>
                            </div>
                          </td>
                        </motion.tr>
                      )}
                    </>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
          <div className="px-4 py-3 border-t border-border text-xs"
            style={{ background: HEADER_BG, color: '#8A9BB5' }}>
            共 <span className="font-bold" style={{ color: '#1A2337' }}>{filtered.length}</span> 位客户
          </div>
        </div>
      )}

      {showAdd && (
        <AddCustomerModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); fetchCustomers(); }}
        />
      )}
    </div>
  );
}
